// A sandboxed frame for HTML we did not write: uploaded HTML blobs and
// agent-authored deliverables. Shared by the Documents viewer and the agents
// inline pane so there is one place the sandbox is spelled.
import React, { useMemo } from "react";

// sandbox="" allows nothing: no script execution, no top navigation, no
// forms, no popups, no same-origin access. The inlined meta-CSP is
// belt-and-suspenders against any sandbox bypass — it pins network origins
// for images/styles/scripts/connect to nothing remote and disables any
// <base> override. Inline ``data:`` images and inline styles still render, so
// a memo with its charts embedded looks like the memo.
const CSP_POLICY =
  "default-src 'none'; script-src 'none'; " +
  "img-src data:; style-src 'unsafe-inline'; base-uri 'none';";

// The parser creates a real head even for fragments. Inserting there avoids
// mistaking a tag in a comment, attribute or title for a placement anchor.
export const injectCsp = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const meta = doc.createElement("meta");
  meta.setAttribute("http-equiv", "Content-Security-Policy");
  meta.setAttribute("content", CSP_POLICY);
  doc.head.prepend(meta);
  // srcdoc never enters quirks mode; <!DOCTYPE html> keeps the parsed shape standard.
  const doctype = doc.doctype === null ? "" : "<!DOCTYPE html>";
  return `${doctype}${doc.documentElement.outerHTML}`;
};

const HtmlIframeView = ({
  html,
  title = "HTML document preview",
}: {
  html: string;
  title?: string;
}) => {
  // Memoized on the text: the host re-renders on every pane drag and every
  // streamed frame, and re-wrapping a megabyte each time is wasted work. The
  // frame never reloads either way — an equal srcdoc string is a no-op.
  const wrapped = useMemo(() => injectCsp(html), [html]);
  return (
    <iframe
      srcDoc={wrapped}
      sandbox=""
      referrerPolicy="no-referrer"
      title={title}
      className="w-full h-full border-0"
    />
  );
};

export default HtmlIframeView;
